import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import PageHeader from '../components/PageHeader.jsx'
import { api } from '../lib/api.js'
import { useAccess } from '../lib/access.jsx'
import { ACTIONS } from '../lib/rbac.js'
import styles from './Reconciliation.module.css'

/**
 * IS THE CHAMPIONSHIP UNIVERSE CLEAN?
 *
 * Brian, 2026-10-18: "Yes it does bother me. We need to get this clean and
 * perfect." Nothing in the estate answered that question. Every number SR
 * had answers a NARROWER one and can read perfect while the object rots:
 * the T+7 SLA counts only records that can trip a clock, so it structurally
 * excludes all 427 blind bodies; the gap count counts only records with a
 * date; the heartbeat counts engines, not data.
 *
 * ⚠️ UNMEASURED RENDERS AS AN EM DASH, NEVER AS 0. The backend sends `null`
 * for anything it did not measure, and a truncated pull sends null for
 * EVERYTHING with a warning attached. A failed read that renders zero has
 * bitten this estate twice — a 45-day window hid 84 huntable records, a
 * dateless filter hid 1,968, both while every indicator read green. A zero
 * is a claim that the work is done and has to be earned.
 *
 * ⚠️ NO DEAD-END FLAGS. Every row carries the one action that resolves it
 * and a link through to the records behind the number. A flag a human
 * cannot act on is a reproach, not a report.
 *
 * READ-ONLY. There is no write path on this page or on the route behind it.
 */

/* `0` is a real answer and must render as 0; `null` means nobody looked.
 * Collapsing them is the defect this page exists to catch, so the falsy
 * check is explicit rather than `value || '—'`. */
const show = (v) => (v === null || v === undefined ? '—' : v.toLocaleString())
const pct = (v) => (v === null || v === undefined ? null : `${v}%`)

export default function Reconciliation() {
  const { can, why } = useAccess()
  const allowed = can(ACTIONS.CC_VIEW)
  const [state, setState] = useState({ status: 'loading', data: null, error: '' })

  const load = useCallback(async (force) => {
    setState((s) => ({ ...s, status: s.data ? 'refreshing' : 'loading', error: '' }))
    try {
      const data = await api.reconciliation({ force })
      setState({ status: 'ready', data, error: '' })
    } catch (e) {
      /* A read that failed must never leave the previous numbers on screen
       * looking current, and must never render as a clean estate. */
      setState({ status: 'error', data: null, error: String(e?.message || e) })
    }
  }, [])

  useEffect(() => { if (allowed) load(false) }, [allowed, load])

  if (!allowed) {
    return (
      <>
        <PageHeader eyebrow="Reconciliation" title="Is the universe clean?" />
        <p className={styles.note}>{why(ACTIONS.CC_VIEW) || 'You do not have access to this view.'}</p>
      </>
    )
  }

  const d = state.data
  const worst = d?.rows?.filter((r) => r.severity === 'critical' && typeof r.n === 'number' && r.n > 0).length

  return (
    <>
      <PageHeader
        eyebrow="Reconciliation"
        title="Is the universe clean?"
        lead={
          state.status === 'error'
            ? 'The readout could not be measured. Every count below is UNKNOWN — do not read this as a clean universe.'
            : 'One screen, read live off the object. Every other number in the estate answers a narrower question and can read perfect while this one does not.'
        }
        actions={
          <button type="button" className={styles.refresh}
            disabled={state.status === 'loading' || state.status === 'refreshing'}
            onClick={() => load(true)}>
            {state.status === 'refreshing' ? 'Re-measuring…' : 'Re-measure'}
          </button>
        }
      />

      {state.status === 'loading' && (
        <p className={styles.note}>Paging the full object — about a minute cold, because a partial read would be worse than a slow one.</p>
      )}

      {state.status === 'error' && (
        <div className={`${styles.banner} ${styles.bad}`}>
          <strong>UNMEASURED, not clean.</strong> {state.error}
          <div className={styles.sub}>Nothing here is a zero. Nobody looked.</div>
        </div>
      )}

      {d && (
        <>
          {d.complete === false && (
            <div className={`${styles.banner} ${styles.bad}`}>
              <strong>The pull truncated.</strong> {d.warning}
            </div>
          )}

          <p className={styles.meta}>
            {show(d.denominator)} records · measured {new Date(d.measuredAt).toLocaleTimeString()}
            {typeof d.ageSeconds === 'number' && ` · ${d.ageSeconds}s old`}
            {typeof worst === 'number' && ` · ${worst} of ${d.rows.length} rows are red`}
          </p>

          <ul className={styles.rows}>
            {d.rows.map((r) => (
              <li key={r.id} className={`${styles.row} ${r.severity === 'critical' ? styles.critical : styles.warning}`}>
                <div className={styles.figure}>
                  <span className={styles.n}>{show(r.n)}</span>
                  {r.of !== undefined && <span className={styles.of}>of {show(r.of)}</span>}
                  {r.pct !== undefined && pct(r.pct) && <span className={styles.of}>{pct(r.pct)}</span>}
                  {r.records !== undefined && <span className={styles.of}>{show(r.records)} records</span>}
                </div>
                <div className={styles.body}>
                  <h2 className={styles.label}>{r.label}</h2>
                  <p className={styles.why}>{r.why}</p>
                  {/* THE CTA IS NOT DECORATION. A row without one is a
                      dead-end flag, which is a standing rule against. */}
                  <p className={styles.cta}><strong>To clear it:</strong> {r.cta}</p>
                  {/* 🔴 THIS LINK USED TO PROMISE `r.n`, AND FOR THE BLIND ROW
                      THAT IS THE WRONG NUMBER. There `n` counts BODIES (427)
                      and the records behind them are 5,071 (both measured
                      2026-10-18), so the link read
                      "See the 427 records →" — wrong by a factor of twelve.
                      It went unnoticed for as long as the link went nowhere,
                      because `&filter=` was inert and nobody could compare the
                      promise against what arrived.

                      `filterMatches` is the count the route will actually
                      return for this row's filter, stated by the route rather
                      than inferred here, so the promise and the destination
                      are the same number by construction.

                      ⚠️ AND AN UNMEASURED COUNT DOES NOT BECOME "the — records".
                      It drops the number rather than printing a dash mid-
                      sentence, because a sentence with an em dash where a
                      figure belongs reads as a rendering bug, not as a
                      deliberate "not measured". */}
                  <Link className={styles.link} to={r.href}>
                    {typeof (r.filterMatches ?? r.n) === 'number'
                      ? `See the ${show(r.filterMatches ?? r.n)} records →`
                      : 'See the records →'}
                  </Link>
                </div>
              </li>
            ))}
          </ul>

          <p className={styles.note}>
            An em dash means UNMEASURED, never zero. A measured zero shows as 0.
          </p>
        </>
      )}
    </>
  )
}
